import type { Project } from "./schema";

type Instance = Project["formInstances"][number];

/** A global profile is identified by its library collection, else its form. */
export function profileKey(project: Project, instance: Instance): string {
  const form = project.forms.find((row) => row.id === instance.formId);
  return form?.profileLibrary?.id || form?.id || instance.formId;
}
export interface DistinctProfile {
  key: string;
  name: string;
  instanceIds: string[];
  /** Project field IDs filled by this profile. */
  fieldIds: Set<string>;
}
/** Profiles the form actually uses: instances that map at least one field. */
export function distinctProfiles(project: Project): DistinctProfile[] {
  const byKey = new Map<string, DistinctProfile>();
  for (const instance of project.formInstances) {
    const targets = Object.values(instance.mapping).filter((id) =>
      project.fields.some((field) => field.id === id),
    );
    if (!targets.length) continue;
    const key = profileKey(project, instance);
    const form = project.forms.find((row) => row.id === instance.formId);
    const entry = byKey.get(key) || {
      key,
      name: form?.name || instance.label || key,
      instanceIds: [],
      fieldIds: new Set<string>(),
    };
    entry.instanceIds.push(instance.id);
    for (const id of targets) entry.fieldIds.add(id);
    byKey.set(key, entry);
  }
  return [...byKey.values()];
}
export interface ProfileSectionConflict {
  groupId: string;
  groupName: string;
  profiles: string[];
}
/**
 * With two or more distinct profiles, one section may only hold variables of
 * one profile (unmapped variables may sit beside them).
 */
export function profileSectionConflicts(
  project: Project,
): ProfileSectionConflict[] {
  const profiles = distinctProfiles(project);
  if (profiles.length < 2) return [];
  return project.groups.flatMap((group) => {
    const fields = project.fields.filter(
      (field) => field.groupId === group.id && field.formDisplay !== "remove",
    );
    const used = profiles.filter((profile) =>
      fields.some((field) => profile.fieldIds.has(field.id)),
    );
    return used.length > 1
      ? [
          {
            groupId: group.id,
            groupName: group.name || "Untitled section",
            profiles: used.map((profile) => profile.name),
          },
        ]
      : [];
  });
}
export function profileConflictMessage(conflicts: ProfileSectionConflict[]) {
  return conflicts
    .map(
      (conflict) =>
        `${conflict.groupName} mixes ${conflict.profiles.join(" and ")}`,
    )
    .join("; ");
}
export interface ProfileSelector {
  /** Stable selection key: "top", "top:<profile>" or "section:<groupId>". */
  id: string;
  profileKey: string;
  profileName: string;
  instanceIds: string[];
  /** Fields this selector may fill; undefined fills every mapped field. */
  fieldIds?: Set<string>;
}
/**
 * Where profile selectors appear. No profile → none. One distinct profile →
 * a single selector at the top. Several → one per section holding that
 * profile's variables (sections choose independently, even for the same
 * profile); mapped variables outside sections get a top selector per profile.
 */
export function profileSelectors(project: Project): {
  top: ProfileSelector[];
  sections: Map<string, ProfileSelector>;
} {
  const profiles = distinctProfiles(project);
  const sections = new Map<string, ProfileSelector>();
  if (!profiles.length) return { top: [], sections };
  if (profiles.length === 1) {
    const [only] = profiles;
    return {
      top: [
        {
          id: "top",
          profileKey: only.key,
          profileName: only.name,
          instanceIds: only.instanceIds,
        },
      ],
      sections,
    };
  }
  const groups = new Set(project.groups.map((group) => group.id));
  const top: ProfileSelector[] = [];
  for (const profile of profiles) {
    for (const group of [...project.groups].sort((a, b) => a.order - b.order)) {
      const fieldIds = new Set(
        project.fields
          .filter(
            (field) =>
              field.groupId === group.id && profile.fieldIds.has(field.id),
          )
          .map((field) => field.id),
      );
      if (!fieldIds.size || sections.has(group.id)) continue;
      sections.set(group.id, {
        id: `section:${group.id}`,
        profileKey: profile.key,
        profileName: profile.name,
        instanceIds: profile.instanceIds,
        fieldIds,
      });
    }
    const loose = new Set(
      project.fields
        .filter(
          (field) =>
            profile.fieldIds.has(field.id) &&
            (!field.groupId || !groups.has(field.groupId)),
        )
        .map((field) => field.id),
    );
    if (loose.size)
      top.push({
        id: `top:${profile.key}`,
        profileKey: profile.key,
        profileName: profile.name,
        instanceIds: profile.instanceIds,
        fieldIds: loose,
      });
  }
  return { top, sections };
}
